import { usePrevious } from "@react-hookz/web";
import { useForkRef } from "@smartsheet/lodestar-utils";
import { callAllHandlers } from "@smartsheet/lodestar-utils";
import {
    AllHTMLAttributes,
    HTMLAttributes,
    KeyboardEventHandler,
    MouseEventHandler,
    Ref,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { ClickAwayListenerProps, useFlyout, UseFlyoutOptions } from "../../flyout";
import { MenuProps } from "../components/Menu";
import { FocusStrategy } from "../components/MenuContainer";
import { useMenuParentContext } from "../context/MenuParentContext";
import { MenuPlacement } from "../types/MenuPlacement";

/**
 * Consider a null menuItem disabled.
 */
const isMenuItemDisabled = (menuItem: HTMLElement | null): boolean =>
    menuItem === null || menuItem.getAttribute("aria-disabled") === "true";

export type UseMenuOptions<TargetElementType extends HTMLElement = HTMLButtonElement> = Omit<
    UseFlyoutOptions<TargetElementType>,
    "placement"
> & {
    /**
     * Function that will be called when the flyout wants to open.
     */
    onOpenRequested: () => void;

    /**
     * A ref that will be applied to the Menu container.
     */
    menuRef?: Ref<HTMLDivElement>;

    /**
     * Specify a direction and alignment to open the Menu relative to the target element.
     */
    placement?: MenuPlacement;
};

export type UseMenuResult<TargetElementType extends HTMLElement = HTMLButtonElement> = {
    getMenuProps: (overrides?: Partial<MenuProps>) => Omit<MenuProps, "clickAwayProps"> & {
        ref: Ref<HTMLDivElement>;
        clickAwayProps?: Omit<ClickAwayListenerProps, "children">;
    };
    getTargetHTMLProps: (overrides?: AllHTMLAttributes<TargetElementType>) => HTMLAttributes<TargetElementType> & {
        ref: Ref<TargetElementType>;
    };
};

const defaultOffset = {
    distance: 8,
} as const;

/**
 * This component connects a Menu and its target.
 * The target could be a MenuItem or any able element.
 */
export const useMenu = <TargetElementType extends HTMLElement = HTMLButtonElement>(
    options: UseMenuOptions<TargetElementType>
): UseMenuResult<TargetElementType> => {
    const {
        targetRef: providedTargetRef = null,
        menuRef: providedMenuRef = null,
        isOpen,
        onOpenRequested: providedOnOpenRequested,
        onCloseRequested: providedOnCloseRequested,
        placement = "right-start",
        offset: providedOffset = defaultOffset,
        popperProps = {},
        ...rest
    } = options;

    const { focusMode, registerSubMenu, parent } = useMenuParentContext();
    const hasParentMenu = !!parent;

    const offset = useMemo(() => {
        return hasParentMenu
            ? {
                  // If this is a submenu, and no override is passed, set the offset to 0 to account for
                  // the Menu's right/left gutter.
                  distance: 0,
                  // Align the sub-menu's first menuitem with the parent menu's menuitem.
                  skidding: -8,
                  // But also allow overrides if they are provided.
                  ...(providedOffset !== defaultOffset ? providedOffset : {}),
              }
            : providedOffset;
    }, [hasParentMenu, providedOffset]);

    // Used to track which item should be focused when the menu is opened.
    const menuRefLocalCopy = useRef<HTMLDivElement>(null);
    const targetRefLocalCopy = useRef<TargetElementType>(null);

    const menuRef = useForkRef<HTMLDivElement>(providedMenuRef, menuRefLocalCopy);
    const targetRef = useForkRef(providedTargetRef, targetRefLocalCopy);
    const [focusStrategy, setFocusStrategy] = useState<FocusStrategy>("none");

    const [dir] = placement.split("-");

    const {
        // We don't want to use aria-owns here - we want to use aria-controls.
        targetProps: { "aria-owns": _0, ...targetProps },
        flyoutProps,
    } = useFlyout({
        ...rest,
        closeOnEscape: true,
        isOpen,
        targetRef,
        onCloseRequested: providedOnCloseRequested,
        placement,
        offset,
        popperProps: {
            ...popperProps,
            modifiers: [...(popperProps.modifiers || []), { name: "preventOverflow" }],
        },
    });

    // Register with the menu context to allow others to request a close on this menu.
    const previousIsOpen = usePrevious(isOpen);
    useEffect(() => {
        if (!previousIsOpen && isOpen) {
            const removeRegistration = registerSubMenu(providedOnCloseRequested);
            return removeRegistration;
        }
    }, [previousIsOpen, isOpen, providedOnCloseRequested, registerSubMenu]);

    // Called when something wants to open the menu.
    const onOpenRequested = useCallback(() => {
        if (isMenuItemDisabled(targetRefLocalCopy.current)) {
            return;
        }
        providedOnOpenRequested();
    }, [providedOnOpenRequested]);

    // Figures out what the menu should do if there's an applicable keydown on its target element.
    const onTargetKeyDown: KeyboardEventHandler<TargetElementType> = useCallback(
        (event) => {
            if (event.defaultPrevented) {
                return;
            }
            // This maps a key to a focusStrategy for the Menu when it opens and iff it should open.
            const keyMap = {
                Enter: "first",
                " ": "first",
                ArrowUp: (dir === "top" || dir === "bottom") && "last",
                ArrowDown: (dir === "bottom" || dir === "top") && "first",
                ArrowRight: dir === "right" && "first",
                ArrowLeft: dir === "left" && "first",
            } as const;
            const newlyOpenedMenuFocusStrategy = keyMap[event.key as keyof typeof keyMap];
            const isTargetDisabled = isMenuItemDisabled(targetRefLocalCopy.current);
            if (newlyOpenedMenuFocusStrategy && !isTargetDisabled) {
                event.preventDefault();
                event.stopPropagation();
                setFocusStrategy(newlyOpenedMenuFocusStrategy);
                onOpenRequested();
                return;
            }
        },
        [onOpenRequested, dir]
    );

    const onTargetMouseEnter: MouseEventHandler<TargetElementType> = useCallback(
        (event) => {
            // Only attach this open-on-hover functionality to sub-menus.
            if (event.defaultPrevented || !hasParentMenu) {
                return;
            }
            // If we are in a sub-menu and the user is in keyboard mode, don't open
            // the hovered item.
            if (focusMode === "keyboard") {
                return;
            }
            setFocusStrategy("none");
            onOpenRequested();
        },
        [focusMode, onOpenRequested, hasParentMenu]
    );

    const onTargetClick: MouseEventHandler<TargetElementType> = useCallback(() => {
        if (!isOpen) {
            // Mouse click, don't focus anything.
            setFocusStrategy("none");
            providedOnOpenRequested();
        } else {
            providedOnCloseRequested();
        }
    }, [isOpen, providedOnCloseRequested, providedOnOpenRequested]);

    const getMenuProps = useCallback<UseMenuResult["getMenuProps"]>(
        ({ role, ...rest } = {}) => {
            return {
                ...flyoutProps,
                ...rest,

                // For some of these, we need to combine them a bit.
                onCloseRequested: () => {
                    rest?.onCloseRequested?.();
                    flyoutProps.onCloseRequested();
                },

                // TODO this is a bit weird...
                placement: rest?.placement || (flyoutProps.placement as MenuPlacement),
                focusStrategy,
                role: role ?? "menu",
                ref: menuRef,
                //the onClickAway noop is so submenus will not close when their parent MenuItems are clicked
                //https://app.smartsheet.com/sheets/J26gVQgGrGQ64wq5q9gRRMr2QcCGV5J8CMVhrgV1?rowId=1052426739552132
                clickAwayProps: hasParentMenu
                    ? {
                          onClickAway: () => {},
                      }
                    : undefined,
            };
        },
        [flyoutProps, focusStrategy, menuRef, hasParentMenu]
    );
    const getTargetHTMLProps = useCallback<UseMenuResult<TargetElementType>["getTargetHTMLProps"]>(
        ({
            "aria-haspopup": ariaHasPopup,
            onClick,
            onMouseEnter,
            onKeyDown,
            // onFocus,
            ...overrides
        } = {}) => {
            // when the target is a subMenu do not include onTargetClick handler
            const onClickHandler = hasParentMenu ? onClick : callAllHandlers(onClick, onTargetClick);
            return {
                ...overrides,
                ...targetProps,
                onClick: onClickHandler,
                onMouseEnter: callAllHandlers(onMouseEnter, onTargetMouseEnter),
                onKeyDown: callAllHandlers(onKeyDown, onTargetKeyDown),
                // onFocus: callAllHandlers(onFocus, onTargetFocus),
                "aria-haspopup": ariaHasPopup ?? "menu",
                "aria-expanded": isOpen,
                "aria-controls": isOpen ? flyoutProps.id : undefined,
            };
        },
        [flyoutProps.id, isOpen, onTargetClick, onTargetKeyDown, onTargetMouseEnter, targetProps, hasParentMenu]
    );

    return {
        getMenuProps,
        getTargetHTMLProps,
    };
};
